// =============================================================================
// DATA MODELER - Main component with React Flow
// =============================================================================

import { useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { PrimitivePalette } from './components/PrimitivePalette';
import { EntityConfigPanel } from './components/EntityConfigPanel';
import { nodeTypes } from './components/nodes';
import { edgeTypes } from './components/edges';
import { useDataModelerStore } from './store';
import type { PrimitiveType } from './types';

const defaultEdgeOptions = {
  type: 'relationEdge',
  animated: false,
};

export function DataModeler() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reactFlowInstance = useRef<any>(null);

  const {
    nodes,
    edges,
    selectedEntityId,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addEntity,
    selectEntity,
    removeEntity,
    duplicateEntity,
    removeRelation,
  } = useDataModelerStore();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onInit = useCallback((instance: any) => {
    reactFlowInstance.current = instance;
  }, []);

  const onDragStart = useCallback(
    (event: React.DragEvent, primitive: PrimitiveType) => {
      event.dataTransfer.setData('application/datamodeler', primitive);
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const primitive = event.dataTransfer.getData('application/datamodeler') as PrimitiveType;

      if (!primitive || !reactFlowWrapper.current || !reactFlowInstance.current) {
        return;
      }

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      addEntity(primitive, position);
    },
    [addEntity]
  );

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      selectEntity(node.id);
    },
    [selectEntity]
  );

  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: { id: string }) => {
      // Select relation for editing
      useDataModelerStore.getState().selectRelation(edge.id);
    },
    []
  );

  const onPaneClick = useCallback(() => {
    selectEntity(null);
  }, [selectEntity]);

  // Enhance nodes with callbacks
  const enhancedNodes = nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      onDelete: removeEntity,
      onDuplicate: duplicateEntity,
    },
  }));

  // Enhance edges with delete callback
  const enhancedEdges = edges.map((edge) => ({
    ...edge,
    data: {
      ...edge.data,
      onDelete: removeRelation,
    },
  }));

  return (
    <div className="flex h-full">
      {/* Left sidebar - Primitive palette */}
      <div className="w-72 border-r border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
        <PrimitivePalette onDragStart={onDragStart} />
      </div>

      {/* Main canvas */}
      <div ref={reactFlowWrapper} className="flex-1">
        <ReactFlow
          nodes={enhancedNodes}
          edges={enhancedEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={onInit}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
          className="bg-secondary-50 dark:bg-secondary-950"
          connectionLineStyle={{ stroke: '#6B7280', strokeWidth: 2 }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            className="bg-secondary-50 dark:bg-secondary-950"
          />
          <Controls className="bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700" />
          <MiniMap
            className="bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700"
            nodeColor={(node) => {
              const data = node.data as unknown as { color?: string };
              return data.color || '#6B7280';
            }}
          />
        </ReactFlow>
      </div>

      {/* Right sidebar - Entity config panel */}
      {selectedEntityId && (
        <div className="w-80 border-l border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
          <EntityConfigPanel />
        </div>
      )}
    </div>
  );
}
